import React from 'react';
import { Formik, Form, Field } from 'formik';
import {
  HtmlString,
  TaggingFieldWithLabel,
  UploadWithLabel,
  withFieldProps,
  withLoading,
} from '@adjust/dashboard-components';

import {
  isRequired,
  validate,
  areAllDomains,
  isDomain,
  ValidationMapper,
} from '@adjust/dashboard-core/lib/validations';
import { GoogleSsoConfig, SsoSettings } from '@api/types';

export interface SsoConfigurationFormValues {
  domains: string[];
}

const validation: ValidationMapper = {
  domains: [
    [isRequired, () => t('field.sso-domain.notice')],
    [areAllDomains, () => t('field.email-domain.error')],
  ],
};

const commaKeyCode = 188;

const UploadLabel = () => (
  <div className="sso-configuration__label file-uploader__label">{t('sso.idp-file.label')}</div>
);

interface SsoConfigurationFormProps {
  config: Nullable<GoogleSsoConfig>;
  parsedSettings: Nullable<SsoSettings>;
  isSubmitting: boolean;
  isEditing: boolean;
  onFileSelect: (params: File[]) => void;
  onSubmit: (params: SsoConfigurationFormValues) => void;
  onCancel: () => void;
  selectedFileName?: string;
  onFileDeselect?: () => void;
}

const SsoConfigurationFormBase = ({
  config,
  isSubmitting,
  isEditing,
  onFileSelect,
  onFileDeselect,
  selectedFileName,
  onSubmit,
  onCancel,
  parsedSettings,
}: SsoConfigurationFormProps) => {
  const initialDomains = config ? config.domains : [];
  const isFileFieldValid = parsedSettings || config;

  return (
    <Formik
      initialValues={{
        domains: initialDomains,
      }}
      onSubmit={values => onSubmit(values)}
    >
      {({ isValid, dirty }) => {
        const isFormDirty = isEditing ? dirty || !!parsedSettings : dirty;

        return (
          <Form className="flex-one flex-box-column without-background content-inner">
            <div className="flex-one panel-content form-row">
              <Field name="domains" validate={validate(validation.domains)}>
                {withFieldProps(
                  <TaggingFieldWithLabel
                    validation={isDomain}
                    label={t('sso.email-domain.label')}
                    info={t('sso.email-domain.description')}
                    placeholder={t('sso.email-domain.example')}
                    taggingTokens={[commaKeyCode]}
                    fixedValues={initialDomains}
                  />,
                )}
              </Field>
              <div className="flex-auto file-uploader without-background">
                <p className="info small">
                  <HtmlString>{t('google-sso.dialog.description')}</HtmlString>
                </p>
                <UploadWithLabel
                  label={<UploadLabel />}
                  selected={selectedFileName ? [selectedFileName] : undefined}
                  onSelect={onFileSelect}
                  onDeselect={onFileDeselect}
                  buttonLabel={t('action.sso.upload-file')}
                  accept=".xml"
                />
              </div>
            </div>

            <div className="flex-auto panel-footer">
              <div className="flex-box-row">
                <button type="button" className="btn flex-one" onClick={onCancel}>
                  {t('action.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-main flex-one"
                  disabled={!isValid || !isFormDirty || !isFileFieldValid || isSubmitting}
                >
                  {t('action.save')}
                </button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export const SsoConfigurationForm = withLoading(SsoConfigurationFormBase);
